import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { APP_DEFAULT_LANGUAGE, AppLanguage } from './i18n.types';
import { APP_TRANSLATIONS } from './i18n.translations';

const STORAGE_KEY = 'app_language';

@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly langSubject = new BehaviorSubject<AppLanguage>(APP_DEFAULT_LANGUAGE);
  readonly lang$ = this.langSubject.asObservable();

  get language(): AppLanguage {
    return this.langSubject.value;
  }

  initFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      this.setLanguage(stored, false);
    } catch {
      this.setLanguage(APP_DEFAULT_LANGUAGE, false);
    }
  }

  setLanguage(lang: string | null | undefined, persist = true): void {
    const normalized = this.normalizeLanguage(lang);
    this.langSubject.next(normalized);
    if (persist) {
      try {
        localStorage.setItem(STORAGE_KEY, normalized);
      } catch {
      }
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = normalized;
    }
  }

  t(key: string, params?: Record<string, string | number>): string {
    const lang = this.language;
    const byLang = APP_TRANSLATIONS[lang] ?? APP_TRANSLATIONS[APP_DEFAULT_LANGUAGE];
    const fallback = APP_TRANSLATIONS[APP_DEFAULT_LANGUAGE];
    const raw = byLang?.[key] ?? fallback?.[key] ?? key;
    return this.interpolate(raw, params);
  }

  has(key: string): boolean {
    const lang = this.language;
    const byLang = APP_TRANSLATIONS[lang] ?? {};
    const fallback = APP_TRANSLATIONS[APP_DEFAULT_LANGUAGE] ?? {};
    return key in byLang || key in fallback;
  }

  private normalizeLanguage(lang: string | null | undefined): AppLanguage {
    const value = String(lang ?? '').toLowerCase().trim();
    if (value === 'id' || value === 'in' || value === 'ind') return 'id';
    if (value === 'es' || value === 'sp' || value === 'spa' || value === 'es-es' || value === 'es_us' || value === 'es-us' || value === 'es_mx' || value === 'es-mx') return 'es';
    return 'en';
  }

  private interpolate(template: string, params?: Record<string, string | number>): string {
    if (!params) return template;
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, name: string) => {
      const value = params[name];
      return value === undefined || value === null ? '' : String(value);
    });
  }
}
